import React, { useState, useMemo } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip,
  ResponsiveContainer, ReferenceLine
} from 'recharts';
import { formatCurrency, monthLabel } from '../../utils/helpers';

// Renders x-axis label as a SINGLE line at -45deg — identical behaviour to the Orders chart tick
const CustomXAxisTick = ({ x, y, payload, fontSize = 11, angle = -45 }) => {
  const text = monthLabel(payload.value); // e.g. "August 2024"
  return (
    <g>
      <text
        x={x}
        y={y}
        textAnchor="end"
        fontSize={fontSize}
        fontWeight={600}
        fill="#374151"
        transform={`rotate(${angle} ${x} ${y})`}
      >
        {text}
      </text>
    </g>
  );
};

const CustomTooltip = ({ active, payload, label, currency }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white border border-gray-100 rounded-lg shadow-lg px-3 py-2 text-sm">
      <p className="font-semibold text-gray-600 mb-1">{monthLabel(label)}</p>
      <p className="font-bold text-green-700">
        {formatCurrency(payload[0]?.value ?? 0, currency)}
      </p>
    </div>
  );
};

const NetRevenueSparkline = ({ chartData, currency = 'GBP', name = 'Net Revenue' }) => {

  const [activeIndex, setActiveIndex] = useState(null);

  const data = useMemo(() =>
    chartData.map(d => ({ month: d.month, netRevenue: d.netRevenue })),
    [chartData]
  );

  const lastIdx = data.length - 1;
  const displayIdx = activeIndex !== null ? activeIndex : lastIdx;

  const displayMonth = data[displayIdx] ? monthLabel(data[displayIdx].month) : '';
  const displayValue = data[displayIdx]?.netRevenue ?? 0;

  return (
    <div className="w-full flex flex-col">
      {/* Sub-header: active month name + value (below card title) */}
      <div className="flex justify-between items-start px-4 pt-2 pb-1">
        <div>
          <div className="text-gray-700 font-semibold text-base leading-tight">{displayMonth}</div>
          <div className="text-xl font-bold text-gray-900 mt-0.5">
            {formatCurrency(displayValue, currency).replace(/^[£$€¥]/, '')}
          </div>
        </div>
      </div>

      {/* Chart — same left:40 margin as Orders chart so first label isn't clipped */}
      <div dir="ltr" className="px-2">
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart
            data={data}
            margin={{ top: 10, right: 20, left: 60, bottom: 75 }}
            onMouseMove={(state) => {
              if (state.isTooltipActive && state.activeTooltipIndex !== undefined) {
                setActiveIndex(state.activeTooltipIndex);
              }
            }}
            onMouseLeave={() => setActiveIndex(null)}
          >
            <defs>
              <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#16a34a" stopOpacity={0.25} />
                <stop offset="95%" stopColor="#16a34a" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="month"
              tick={<CustomXAxisTick fontSize={11} angle={-45} />}
              tickMargin={10}
              interval={0}
              axisLine={false}
              tickLine={false}
            />
            <YAxis domain={[0, (dataMax) => Math.ceil(dataMax * 1.15)]} hide={true} />
            <Tooltip
              content={<CustomTooltip currency={currency} />}
              cursor={{ stroke: '#16a34a', strokeWidth: 2, strokeDasharray: 'none' }}
            />
            {data[displayIdx] && (
              <ReferenceLine
                x={data[displayIdx].month}
                stroke="#16a34a"
                strokeWidth={2}
              />
            )}
            <Area
              type="monotone"
              dataKey="netRevenue"
              stroke="#16a34a"
              strokeWidth={3}
              fill="url(#revenueGradient)"
              name={name}

              dot={{ fill: '#16a34a', r: 4, strokeWidth: 0 }}
              activeDot={{ r: 6, fill: '#16a34a', stroke: '#fff', strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default NetRevenueSparkline;
